import React, { memo, useState, useEffect } from 'react';
import {
  Text,
  Button,
  Space,
  Conditional,
} from '@cashfree-intl/coherent';

import CustomIcon from 'components/CustomIcons';
import AddToWallet from 'components/AddToWallet/AddToWallet';

import EVENTS from 'constants/analytics';
import Analytics from 'utils/analytics';

import {
  ADD_TO_WALLET,
  BANNER_TEXT,
  DISPUTE_WALLET_METADATA,
  RECHARGE_IN_PROGRESS,
  REFUND_WALLET_METADATA,
} from '../constants';

import RechargeInProgress from './RechargeInProgress';
import { IWalletsIntro } from '../types';
import { StyledBannerCard, WalletIntroCardsPaper } from '../styled';

const WalletIntro = ({
  bankDetails,
  handleAddMoneyToWalletClick,
  getWalletInfo,
  refundWalletActive,
  disputeWalletActive,
  showDisputesWallet,
  bankList
}: IWalletsIntro) => {
  const [activeModalName, setActiveModalName] = useState<string>('');
  const [activeModalData, setActiveModalData] = useState<{ walletType?: string }>({});
  const [walletCards, setWalletCards] = useState([]);

  useEffect(() => {
    const wallets = [];
    if (!refundWalletActive) {
      wallets.push(REFUND_WALLET_METADATA);
    }
    if (!disputeWalletActive && showDisputesWallet) {
      wallets.push(DISPUTE_WALLET_METADATA);
    }
    setWalletCards(wallets);
  }, [refundWalletActive, disputeWalletActive]);

  const handleAddToWalletClick = (walletType: string) => () => {
    Analytics.track(EVENTS.WALLETS.REFUND_WALLET.CREATE_WALLET);
    handleAddMoneyToWalletClick(walletType);
    setActiveModalName(ADD_TO_WALLET);
    setActiveModalData({ walletType });
  };

  const closeModal = () => {
    setActiveModalName('');
    setActiveModalData({});
    getWalletInfo();
  };


  const confirmRechargeClickHandler = (walletType: string) => {
    setActiveModalName(RECHARGE_IN_PROGRESS);
    setActiveModalData({ walletType });
  };

  const handleCloseRechargeInProgressModal = () => {
    getWalletInfo();
    closeModal();
  };

  // will move the below inline style to styled once there is clarity on the banner

  const bannerMargin = () => {
    return '0 -32px';
  }

  return (
    <>
      <div
        style={{
          background: 'linear-gradient(108deg, #240353 0%, #622FC7 100%)',
          margin: bannerMargin(),
        }}
        className="pl-6 pt-3 pr-6 pb-4"
      >
        <Space justifyContent="center">
          <Text variant="h28" color="white" strong>
            Unlock the Power of Refund Wallets
          </Text>
        </Space>
        <Space justifyContent="center" gap={25} className="pl-6 pr-6 pt-2">
          {BANNER_TEXT.map(data =>
            <StyledBannerCard direction="column" key={data.title}>
              <CustomIcon componentName="STARS" />
              <Text variant="h16" color="white" strong>{data.title}</Text>
              <Text variant="p14" color="white">{data.description}</Text>
            </StyledBannerCard>
          )}
        </Space>
      </div>
      <Space gap={3} className="mt-4">
        {walletCards.map(card => {
          return (
            <WalletIntroCardsPaper
              key={card.key}
            >
              <CustomIcon componentName={card.iconName} />
              <Text variant="h20" className="mt-2">
                {card.heading}
              </Text>
              <Text variant="b12" color="bodyLight" className="mt-1">
                Keep this recharged so that your customers never have to wait
                for a refund.
              </Text>
              <Button
                primary
                className="mt-2"
                onClick={handleAddToWalletClick(card.heading)}
              >
                Add Money
              </Button>
            </WalletIntroCardsPaper>
          );
        })}
      </Space>
      <Conditional if={activeModalName === ADD_TO_WALLET && Object.keys(bankDetails).length > 1}>
        <AddToWallet
          open={activeModalName === ADD_TO_WALLET}
          walletType={activeModalData?.walletType}
          closeModal={closeModal}
          confirmRechargeClickHandler={() =>
            confirmRechargeClickHandler(activeModalData?.walletType)
          }
          isWalletsIntro
          bankDetails={bankDetails}
          bankList={bankList}
        />
      </Conditional>
      <Conditional if={activeModalName === RECHARGE_IN_PROGRESS}>
        <RechargeInProgress
          open={activeModalName === RECHARGE_IN_PROGRESS}
          walletType={activeModalData?.walletType}
          closeModal={handleCloseRechargeInProgressModal}
        />
      </Conditional>
    </>
  );
};

export default memo(WalletIntro);
